import { Link, useParams } from 'react-router-dom';
import PageLayout from '@/components/feature/PageLayout';
import { useSeo } from '@/hooks/useSeo';
import { useJsonLd } from '@/hooks/useJsonLd';
import { characters } from '@/mocks/characters';
import { categories } from '@/mocks/categories';
import { seiyuuList } from '@/mocks/seiyuu';

export default function CharacterDetail() {
  const { id } = useParams();
  const char = characters.find((c) => c.id === id) ?? characters[0];
  const seiyuu = seiyuuList.find((s) => s.id === char.seiyuu);
  const related = characters.filter((c) => c.workId === char.workId && c.id !== char.id).slice(0, 4);

  useSeo({
    title: `${char.name} - ${char.title} | ${char.work}角色百科`,
    description: `${char.name}是${char.work}中的${char.category}，${char.summary} 配音：${char.seiyuuName}。在妖精动漫网查看详细动漫人物介绍与声优表信息。`,
  });

  useJsonLd({
    '@context': 'https://schema.org',
    '@type': 'Person',
    name: char.name,
    description: `${char.name} - ${char.title}，出自${char.work}的${char.category}角色，${char.summary}`,
    image: char.image,
    knowsAbout: [char.category, char.work],
  });

  const cat = categories.find((c) => c.id === char.categoryId);

  return (
    <PageLayout>
      <div className="mx-auto max-w-7xl px-4 md:px-6 pt-24 md:pt-28 pb-14">
        {/* 面包屑 */}
        <nav aria-label="面包屑" className="mb-6 flex flex-wrap items-center gap-1.5 text-xs text-foreground-500">
          <Link to="/" className="hover:text-primary-700">首页</Link>
          <i className="ri-arrow-right-s-line" />
          <Link to="/characters" className="hover:text-primary-700">角色百科</Link>
          <i className="ri-arrow-right-s-line" />
          <span className="text-foreground-800">{char.name}</span>
        </nav>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* 角色图 */}
          <div className="lg:col-span-4">
            <div className="sticky top-24 overflow-hidden rounded-2xl border border-background-200/70 bg-background-50">
              <div className="relative aspect-[3/4]">
                <img src={char.image} alt={`${char.name} - ${char.title}`} className="h-full w-full object-cover object-top" />
              </div>
              <div className="flex items-center justify-between p-4">
                <span className="flex items-center gap-1 text-sm text-foreground-600">
                  <i className="ri-fire-line text-accent-600" />
                  人气 {char.popularity}%
                </span>
                <span className="flex items-center gap-1 text-sm text-foreground-600">
                  <i className="ri-trophy-line text-accent-600" />
                  No.{char.rank}
                </span>
              </div>
            </div>
          </div>

          {/* 信息 */}
          <div className="lg:col-span-8">
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded-full bg-secondary-100 px-3 py-1 text-xs text-secondary-800">{char.category}</span>
              <span className="rounded-full bg-primary-100 px-3 py-1 text-xs text-primary-700">{char.element}系</span>
              {char.tags.map((t) => (
                <span key={t} className="rounded-full bg-background-100 px-3 py-1 text-xs text-foreground-600">{t}</span>
              ))}
            </div>

            <h1 className="mt-4 font-heading text-3xl md:text-4xl font-black text-foreground-950">{char.name}</h1>
            <p className="mt-1 text-sm text-foreground-500">{char.alias}</p>
            <p className="mt-3 text-lg font-medium text-primary-700">{char.title}</p>

            {/* 信息卡 */}
            <dl className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3">
              {[
                { label: '所属作品', value: char.work },
                { label: '角色分类', value: char.category },
                { label: '属性元素', value: char.element },
                { label: '配音演员', value: char.seiyuuName },
                { label: '人气榜', value: `No.${char.rank}` },
                { label: '标签', value: char.tags.slice(0, 2).join(' / ') },
              ].map((info) => (
                <div key={info.label} className="rounded-xl border border-background-200/70 bg-background-50 p-4">
                  <dt className="text-xs text-foreground-500">{info.label}</dt>
                  <dd className="mt-1 truncate text-sm font-medium text-foreground-950">{info.value}</dd>
                </div>
              ))}
            </dl>

            <div className="mt-6 rounded-xl border border-background-200/70 bg-background-50 p-5">
              <h2 className="font-heading text-lg font-bold text-foreground-950">角色介绍</h2>
              <p className="mt-3 text-sm md:text-base leading-7 text-foreground-700">{char.description}</p>
            </div>

            {/* 声优信息 */}
            {seiyuu && (
              <div className="mt-6 rounded-xl border border-primary-200/70 bg-primary-100/50 p-5">
                <h2 className="font-heading text-lg font-bold text-foreground-950">配音演员 · {seiyuu.name}</h2>
                <div className="mt-4 flex flex-col sm:flex-row gap-4">
                  <img src={seiyuu.image} alt={`声优 ${seiyuu.name}`} className="h-20 w-20 shrink-0 rounded-full object-cover ring-2 ring-accent-300" />
                  <div className="text-sm leading-6 text-foreground-700">
                    <p><strong className="text-foreground-950">出道：</strong>{seiyuu.debut} · <strong className="text-foreground-950">擅长：</strong>{seiyuu.specialty}</p>
                    <p className="mt-1"><strong className="text-foreground-950">代表作：</strong>{seiyuu.representative.map((r) => `${r.role}（${r.work}）`).join('、')}</p>
                  </div>
                </div>
              </div>
            )}

            {/* 相关推荐 */}
            {related.length > 0 && (
              <div className="mt-8">
                <h2 className="font-heading text-lg font-bold text-foreground-950 mb-4">同作品角色推荐</h2>
                <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                  {related.map((r) => (
                    <Link key={r.id} to={`/character/${r.id}`} className="group overflow-hidden rounded-xl border border-background-200/70 bg-background-50">
                      <div className="aspect-[3/4] overflow-hidden">
                        <img src={r.image} alt={r.name} className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105" />
                      </div>
                      <div className="p-3">
                        <p className="truncate text-sm font-medium text-foreground-950">{r.name}</p>
                        <p className="truncate text-xs text-foreground-500">{r.title}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* 分类导航 */}
        {cat && (
          <div className="mt-10 flex flex-wrap items-center gap-2 rounded-xl border border-background-200/70 bg-background-100 p-4 text-sm">
            <span className="text-foreground-600">继续探索：</span>
            <Link to={`/characters?c=${cat.id}`} className="rounded-full bg-background-50 px-3 py-1 text-foreground-700 hover:bg-primary-100 hover:text-primary-700 transition-colors">{cat.name}</Link>
            {char.tags.slice(0, 3).map((t) => (
              <Link key={t} to={`/characters?q=${encodeURIComponent(t)}`} className="rounded-full bg-background-50 px-3 py-1 text-foreground-700 hover:bg-primary-100 hover:text-primary-700 transition-colors">{t}</Link>
            ))}
            <Link to="/seiyuu" className="rounded-full bg-background-50 px-3 py-1 text-foreground-700 hover:bg-primary-100 hover:text-primary-700 transition-colors">查看完整声优表</Link>
          </div>
        )}
      </div>
    </PageLayout>
  );
}